/**
 * Agents API Service
 * Handles all agent spec, agent run, and agent proposal operations
 *
 * NOTE: Demo mode has been moved to vh-demo-web.
 * All data comes from production API.
 */

import api from './api/client';
import logger from '../utils/logger';

/**
 * Agents API Service
 * Handles all agent spec, agent run, and agent proposal operations
 */
const agentsApi = {
  /**
   * Get agent specs
   * @param {Object} filters - Optional filters (role, enabled, tenantId)
   * @returns {Promise<Array>} Agent specs
   */
  async getAgentSpecs(filters = {}) {
    try {
      const queryString = new URLSearchParams(filters).toString();
      const url = queryString ? `/api/agents/specs?${queryString}` : '/api/agents/specs';
      return await api.get(url);
    } catch (error) {
      logger.error('[Agents API] Failed to get specs', { error: error.message });
      if (error.status === 403) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Get agent spec by ID
   * @param {string} agentId - Agent ID
   * @returns {Promise<Object>} Agent spec
   */
  async getAgentSpec(agentId) {
    try {
      return await api.get(`/api/agents/specs/${agentId}`);
    } catch (error) {
      logger.error('[Agents API] Failed to get spec', { agentId, error: error.message });
      throw error;
    }
  },

  /**
   * Create agent spec
   * @param {Object} data - Agent spec data
   * @returns {Promise<Object>} Created agent spec
   */
  async createAgentSpec(data) {
    try {
      return await api.post('/api/agents/specs', data);
    } catch (error) {
      logger.error('[Agents API] Failed to create spec', { error: error.message });
      throw error;
    }
  },

  /**
   * Update agent spec
   * @param {string} agentId - Agent ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} Updated agent spec
   */
  async updateAgentSpec(agentId, data) {
    try {
      return await api.put(`/api/agents/specs/${agentId}`, data);
    } catch (error) {
      logger.error('[Agents API] Failed to update spec', { agentId, error: error.message });
      throw error;
    }
  },

  /**
   * Toggle agent spec enabled status
   * @param {string} agentId - Agent ID
   * @param {boolean} enabled - Enabled status
   * @returns {Promise<Object>} Updated agent spec
   */
  async toggleAgentSpec(agentId, enabled) {
    try {
      return await api.patch(`/api/agents/specs/${agentId}/toggle`, { enabled });
    } catch (error) {
      logger.error('[Agents API] Failed to toggle spec', { agentId, error: error.message });
      throw error;
    }
  },

  /**
   * Get agent runs
   * @param {Object} filters - Optional filters (agentId, status, trigger, dateRange, limit)
   * @returns {Promise<Array>} Agent runs
   */
  async getAgentRuns(filters = {}) {
    try {
      const queryString = new URLSearchParams(filters).toString();
      const url = queryString ? `/api/agents/runs?${queryString}` : '/api/agents/runs';
      return await api.get(url);
    } catch (error) {
      logger.error('[Agents API] Failed to get runs', { error: error.message });
      if (error.status === 403) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Get agent run by ID
   * @param {string} runId - Agent run ID
   * @returns {Promise<Object>} Agent run
   */
  async getAgentRun(runId) {
    try {
      return await api.get(`/api/agents/runs/${runId}`);
    } catch (error) {
      logger.error('[Agents API] Failed to get run', { runId, error: error.message });
      throw error;
    }
  },

  /**
   * Trigger agent run
   * @param {string} agentId - Agent ID
   * @param {Object} context - Context data for the run
   * @returns {Promise<Object>} Created agent run
   */
  async triggerAgentRun(agentId, context = {}) {
    try {
      return await api.post('/api/agents/runs', { agentId, context });
    } catch (error) {
      logger.error('[Agents API] Failed to trigger run', { agentId, error: error.message });
      throw error;
    }
  },

  /**
   * Get agent run statistics
   * @param {string} agentId - Agent ID
   * @param {Object} dateRange - Optional date range filter
   * @returns {Promise<Object>} Run statistics
   */
  async getAgentRunStats(agentId, dateRange = {}) {
    try {
      const queryString = new URLSearchParams({ agentId, ...dateRange }).toString();
      return await api.get(`/api/agents/runs/stats?${queryString}`);
    } catch (error) {
      logger.error('[Agents API] Failed to get run stats', { agentId, error: error.message });
      if (error.status === 403) {
        return {
          total: 0,
          succeeded: 0,
          failed: 0,
          successRate: 0,
          totalCost: 0,
          totalTokens: 0,
          avgCost: 0,
          avgTokens: 0,
        };
      }
      throw error;
    }
  },

  /**
   * Get agent proposals
   * @param {Object} filters - Optional filters (status, priority, agentId)
   * @returns {Promise<Array>} Agent proposals
   */
  async getAgentProposals(filters = {}) {
    try {
      const queryString = new URLSearchParams(filters).toString();
      const url = queryString ? `/api/agent-proposals?${queryString}` : '/api/agent-proposals';
      return await api.get(url);
    } catch (error) {
      logger.error('[Agents API] Failed to get proposals', { error: error.message });
      if (error.status === 403) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Get agent proposal by ID
   * @param {string} proposalId - Proposal ID
   * @returns {Promise<Object>} Agent proposal
   */
  async getAgentProposal(proposalId) {
    try {
      return await api.get(`/api/agent-proposals/${proposalId}`);
    } catch (error) {
      logger.error('[Agents API] Failed to get proposal', { proposalId, error: error.message });
      throw error;
    }
  },

  /**
   * Approve agent proposal
   * @param {string} proposalId - Proposal ID
   * @param {string} comments - Optional approval comments
   * @returns {Promise<Object>} Updated proposal
   */
  async approveProposal(proposalId, comments = '') {
    try {
      return await api.post(`/api/agent-proposals/${proposalId}/approve`, { comments });
    } catch (error) {
      logger.error('[Agents API] Failed to approve proposal', { proposalId, error: error.message });
      throw error;
    }
  },

  /**
   * Reject agent proposal
   * @param {string} proposalId - Proposal ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Updated proposal
   */
  async rejectProposal(proposalId, reason) {
    try {
      return await api.post(`/api/agent-proposals/${proposalId}/reject`, { reason });
    } catch (error) {
      logger.error('[Agents API] Failed to reject proposal', { proposalId, error: error.message });
      throw error;
    }
  },
};

export default agentsApi;
